import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';

import { toast } from 'react-toastify';
import { logger } from 'RootShared/utility/ErrorLogger';

import Dashboard from '../../components/Dashboard';
import TableTree from 'Components/Admin/TableTree';
import strToBool from 'Shared/util/strToBool';
import createPeriod from '../../utilityReporting/createPeriod';
import {
  createTreeArr,
  createTreeArrProject,
  createTreeArrCurrency,
} from '../../utilityReporting/createTreeArrIncomeStatement';

import { ALL_CURRENCIES, ONE_CURRENCY } from '../../constants/constants';

import {
  queryRevenue,
  queryRevenueByCategory,
  queryOtherIncome,
  queryOtherIncomeByCategory,
  queryDirectCosts,
  querySalariesAssignedEmployees,
  queryProjectExpenses,
  queryGrossIncome,
  queryIndirectCosts,
  queryAdministrativePersonnel,
  queryEmployeesOnBench,
  queryExpenses,
  queryExpensesByCategory,
  queryNetIncome,
  queryRevenueByProject,
  queryRevenueByPC,
  queryDirectCostsByProject,
  querySalariesByProject,
  querySalariesBPE,
  queryExpensesByProject,
  queryExpensesBPC,
  queryGrossIncomeByProject,
} from './query';
import useDocumentTitle from 'Shared/hooks/useDocumentTitle';
import usePreserveSearchParams from 'Shared/hooks/usePreserveSearchParams';
import { extractPeriodArray } from '../../utilityReporting/treeCreationMethods';
import useInterval from '../../hooks/useInterval';

const toastParams = {
  autoClose: 3000,
  closeButton: true,
  hideProgressBar: false,
};

const INCOME_STATEMENT_FILTERS = 'income-statement-filters';
const MAX_NUMBER_OF_INTERVAL = 3;

function IncomeStatement(props) {
  const history = useHistory();
  const controller = useRef(new AbortController());
  const [searchParams, setSearchParams] = usePreserveSearchParams(INCOME_STATEMENT_FILTERS);

  useDocumentTitle('Reporting - Income Statement');

  const [period, setPeriod] = useState(searchParams.get('period') || 'MONTH');
  const [currency, setCurrency] = useState(searchParams.get('currency') || 'USD');
  const [isProjectSelected, setIsProjectSelected] = useState(strToBool(searchParams.get('by-project')));
  const [render, setRender] = useState(false);
  const [dateColumnsPeriod, setDateColumnsPeriod] = useState(createPeriod(period + 's'));
  const [showPercent, setShowPercent] = useState(false);
  const doubleGraphQLError = useRef('');
  const doubleNetworkError = useRef('');

  const handlePerception = () => {
    setShowPercent((prev) => !prev);
  };

  const displayErrorToast = (massage) =>
    toast(massage, {
      type: toast.TYPE.ERROR,
      ...toastParams,
    });

  const handleError = (doubleError, error) => {
    if (doubleError.current !== error.message) {
      displayErrorToast(error.message);
      logger.error(error);
    }
    doubleError.current = error.message;
  };

  const queryParams = {
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      !render && setRender(true);
    },
    onError({ networkError, graphQLErrors }) {
      networkError?.result?.errors &&
        networkError.result.errors.forEach((error) => {
          handleError(doubleNetworkError, error);
        });
      graphQLErrors &&
        graphQLErrors.forEach((error) => {
          handleError(doubleGraphQLError, error);
        });
    },
    pollInterval: 30000,
  };

  //Default scenario
  const [getRevenue, { data: dataRevenue, error: errorRevenue, stopPolling: stopRevenue }] = useLazyQuery(
    queryRevenue,
    queryParams
  );
  const [
    getRevenueByCategory,
    { data: dataRevenueByCategory, error: errorRevenueByCategory, stopPolling: stopRevenueByCategory },
  ] = useLazyQuery(queryRevenueByCategory, queryParams);
  const [
    getOtherIncome,
    { data: dataOtherIncome, error: errorOtherIncome, stopPolling: stopOtherIncome },
  ] = useLazyQuery(queryOtherIncome, queryParams);
  const [
    getOtherIncomeByCategory,
    { data: dataOtherIncomeByCategory, error: errorOtherIncomeByCategory, stopPolling: stopOtherIncomeByCategory },
  ] = useLazyQuery(queryOtherIncomeByCategory, queryParams);
  const [
    getDirectCosts,
    { data: dataDirectCosts, error: errorDirectCosts, stopPolling: stopDirectCosts },
  ] = useLazyQuery(queryDirectCosts, queryParams);
  const [
    getSalariesAssignedEmployees,
    {
      data: dataSalariesAssignedEmployees,
      error: errorSalariesAssignedEmployees,
      stopPolling: stopSalariesAssignedEmployees,
    },
  ] = useLazyQuery(querySalariesAssignedEmployees, queryParams);
  const [
    getProjectExpenses,
    { data: dataProjectExpenses, error: errorProjectExpenses, stopPolling: stopProjectExpenses },
  ] = useLazyQuery(queryProjectExpenses, queryParams);
  const [
    getGrossIncome,
    { data: dataGrossIncome, error: errorGrossIncome, stopPolling: stopGrossIncome },
  ] = useLazyQuery(queryGrossIncome, queryParams);
  const [
    getIndirectCosts,
    { data: dataIndirectCosts, error: errorIndirectCosts, stopPolling: stopIndirectCosts },
  ] = useLazyQuery(queryIndirectCosts, queryParams);
  const [
    getAdministrativePersonnel,
    {
      data: dataAdministrativePersonnel,
      error: errorAdministrativePersonnel,
      stopPolling: stopAdministrativePersonnel,
    },
  ] = useLazyQuery(queryAdministrativePersonnel, queryParams);
  const [
    getEmployeesOnBench,
    { data: dataEmployeesOnBench, error: errorEmployeesOnBench, stopPolling: stopEmployeesOnBench },
  ] = useLazyQuery(queryEmployeesOnBench, queryParams);
  const [getExpenses, { data: dataExpenses, error: errorExpenses, stopPolling: stopExpenses }] = useLazyQuery(
    queryExpenses,
    queryParams
  );
  const [
    getExpensesByCategory,
    { data: dataExpensesByCategory, error: errorExpensesByCategory, stopPolling: stopExpensesByCategory },
  ] = useLazyQuery(queryExpensesByCategory, queryParams);
  const [getNetIncome, { data: dataNetIncome, error: errorNetIncome, stopPolling: stopNetIncome }] = useLazyQuery(
    queryNetIncome,
    queryParams
  );

  //By project scenario
  const [
    getRevenueByProject,
    { data: dataRevenueByProject, error: errorRevenueByProject, stopPolling: stopRevenueByProject },
  ] = useLazyQuery(queryRevenueByProject, queryParams);
  const [
    getRevenueByPC,
    { data: dataRevenueByPC, error: errorRevenueByPC, stopPolling: stopRevenueByPC },
  ] = useLazyQuery(queryRevenueByPC, queryParams);
  const [
    getDirectCostsByProject,
    { data: dataDirectCostsByProject, error: errorDirectCostsByProject, stopPolling: stopDirectCostsByProject },
  ] = useLazyQuery(queryDirectCostsByProject, queryParams);
  const [
    getSalariesByProject,
    { data: dataSalariesByProject, error: errorSalariesByProject, stopPolling: stopSalariesByProject },
  ] = useLazyQuery(querySalariesByProject, queryParams);
  const [
    getSalariesBPE,
    { data: dataSalariesBPE, error: errorSalariesBPE, stopPolling: stopSalariesBPE },
  ] = useLazyQuery(querySalariesBPE, queryParams);
  const [
    getExpensesByProject,
    { data: dataExpensesByProject, error: errorExpensesByProject, stopPolling: stopExpensesByProject },
  ] = useLazyQuery(queryExpensesByProject, queryParams);
  const [
    getExpensesBPC,
    { data: dataExpensesBPC, error: errorExpensesBPC, stopPolling: stopExpensesBPC },
  ] = useLazyQuery(queryExpensesBPC, queryParams);
  const [
    getGrossIncomeByProject,
    { data: dataGrossIncomeByProject, error: errorGrossIncomeByProject, stopPolling: stopGrossIncomeByProject },
  ] = useLazyQuery(queryGrossIncomeByProject, queryParams);

  const stopPollingByProject = useCallback(() => {
    (dataRevenueByProject || errorRevenueByProject) && stopRevenueByProject();
    (dataRevenueByPC || errorRevenueByPC) && stopRevenueByPC();
    (dataDirectCostsByProject || errorDirectCostsByProject) && stopDirectCostsByProject();
    (dataSalariesByProject || errorSalariesByProject) && stopSalariesByProject();
    (dataSalariesBPE || errorSalariesBPE) && stopSalariesBPE();
    (dataExpensesByProject || errorExpensesByProject) && stopExpensesByProject();
    (dataExpensesBPC || errorExpensesBPC) && stopExpensesBPC();
    (dataGrossIncomeByProject || errorGrossIncomeByProject) && stopGrossIncomeByProject();
  }, [
    dataDirectCostsByProject,
    dataExpensesBPC,
    dataExpensesByProject,
    dataGrossIncomeByProject,
    dataRevenueByPC,
    dataRevenueByProject,
    dataSalariesBPE,
    dataSalariesByProject,
    errorDirectCostsByProject,
    errorExpensesBPC,
    errorExpensesByProject,
    errorGrossIncomeByProject,
    errorRevenueByPC,
    errorRevenueByProject,
    errorSalariesBPE,
    errorSalariesByProject,
    stopDirectCostsByProject,
    stopExpensesBPC,
    stopExpensesByProject,
    stopGrossIncomeByProject,
    stopRevenueByPC,
    stopRevenueByProject,
    stopSalariesBPE,
    stopSalariesByProject,
  ]);

  const stopPollingByCurrency = useCallback(() => {
    (dataRevenue || errorRevenue) && stopRevenue();
    (dataRevenueByCategory || errorRevenueByCategory) && stopRevenueByCategory();
    (dataOtherIncome || errorOtherIncome) && stopOtherIncome();
    (dataOtherIncomeByCategory || errorOtherIncomeByCategory) && stopOtherIncomeByCategory();
    (dataDirectCosts || errorDirectCosts) && stopDirectCosts();
    (dataSalariesAssignedEmployees || errorSalariesAssignedEmployees) && stopSalariesAssignedEmployees();
    (dataProjectExpenses || errorProjectExpenses) && stopProjectExpenses();
    (dataGrossIncome || errorGrossIncome) && stopGrossIncome();
    (dataIndirectCosts || errorIndirectCosts) && stopIndirectCosts();
    (dataAdministrativePersonnel || errorAdministrativePersonnel) && stopAdministrativePersonnel();
    (dataEmployeesOnBench || errorEmployeesOnBench) && stopEmployeesOnBench();
    (dataExpenses || errorExpenses) && stopExpenses();
    (dataExpensesByCategory || errorExpensesByCategory) && stopExpensesByCategory();
    (dataNetIncome || errorNetIncome) && stopNetIncome();
  }, [
    dataAdministrativePersonnel,
    dataDirectCosts,
    dataEmployeesOnBench,
    dataExpenses,
    dataExpensesByCategory,
    dataGrossIncome,
    dataIndirectCosts,
    dataNetIncome,
    dataOtherIncome,
    dataOtherIncomeByCategory,
    dataProjectExpenses,
    dataRevenue,
    dataRevenueByCategory,
    dataSalariesAssignedEmployees,
    errorAdministrativePersonnel,
    errorDirectCosts,
    errorEmployeesOnBench,
    errorExpenses,
    errorExpensesByCategory,
    errorGrossIncome,
    errorIndirectCosts,
    errorNetIncome,
    errorOtherIncome,
    errorOtherIncomeByCategory,
    errorProjectExpenses,
    errorRevenue,
    errorRevenueByCategory,
    errorSalariesAssignedEmployees,
    stopAdministrativePersonnel,
    stopDirectCosts,
    stopEmployeesOnBench,
    stopExpenses,
    stopExpensesByCategory,
    stopGrossIncome,
    stopIndirectCosts,
    stopNetIncome,
    stopOtherIncome,
    stopOtherIncomeByCategory,
    stopProjectExpenses,
    stopRevenue,
    stopRevenueByCategory,
    stopSalariesAssignedEmployees,
  ]);

  const [stopPolling, resetInterval] = useInterval(MAX_NUMBER_OF_INTERVAL, period, isProjectSelected);

  useEffect(() => {
    if (stopPolling) {
      stopPollingByCurrency();
      stopPollingByProject();
    }

    if (
      (dataRevenue || errorRevenue) &&
      (dataRevenueByCategory || errorRevenueByCategory) &&
      (dataOtherIncome || errorOtherIncome) &&
      (dataOtherIncomeByCategory || errorOtherIncomeByCategory) &&
      (dataDirectCosts || errorDirectCosts) &&
      (dataSalariesAssignedEmployees || errorSalariesAssignedEmployees) &&
      (dataProjectExpenses || errorProjectExpenses) &&
      (dataGrossIncome || errorGrossIncome) &&
      (dataIndirectCosts || errorIndirectCosts) &&
      (dataAdministrativePersonnel || errorAdministrativePersonnel) &&
      (dataEmployeesOnBench || errorEmployeesOnBench) &&
      (dataExpenses || errorExpenses) &&
      (dataExpensesByCategory || errorExpensesByCategory) &&
      (dataNetIncome || errorNetIncome)
    ) {
      resetInterval();
      stopPollingByCurrency();
    }

    if (
      (dataRevenueByProject || errorRevenueByProject) &&
      (dataRevenueByPC || errorRevenueByPC) &&
      (dataDirectCostsByProject || errorDirectCostsByProject) &&
      (dataSalariesByProject || errorSalariesByProject) &&
      (dataSalariesBPE || errorSalariesBPE) &&
      (dataExpensesByProject || errorExpensesByProject) &&
      (dataExpensesBPC || errorExpensesBPC) &&
      (dataGrossIncomeByProject || errorGrossIncomeByProject)
    ) {
      resetInterval();
      stopPollingByProject();
    }
  }, [
    dataAdministrativePersonnel,
    dataDirectCosts,
    dataDirectCostsByProject,
    dataEmployeesOnBench,
    dataExpenses,
    dataExpensesBPC,
    dataExpensesByCategory,
    dataExpensesByProject,
    dataGrossIncome,
    dataGrossIncomeByProject,
    dataIndirectCosts,
    dataNetIncome,
    dataOtherIncome,
    dataOtherIncomeByCategory,
    dataProjectExpenses,
    dataRevenue,
    dataRevenueByCategory,
    dataRevenueByPC,
    dataRevenueByProject,
    dataSalariesAssignedEmployees,
    dataSalariesBPE,
    dataSalariesByProject,
    errorAdministrativePersonnel,
    errorDirectCosts,
    errorDirectCostsByProject,
    errorEmployeesOnBench,
    errorExpenses,
    errorExpensesBPC,
    errorExpensesByCategory,
    errorExpensesByProject,
    errorGrossIncome,
    errorGrossIncomeByProject,
    errorIndirectCosts,
    errorNetIncome,
    errorOtherIncome,
    errorOtherIncomeByCategory,
    errorProjectExpenses,
    errorRevenue,
    errorRevenueByCategory,
    errorRevenueByPC,
    errorRevenueByProject,
    errorSalariesAssignedEmployees,
    errorSalariesBPE,
    errorSalariesByProject,
    resetInterval,
    stopPolling,
    stopPollingByCurrency,
    stopPollingByProject,
    stopSalariesAssignedEmployees,
  ]);

  const request = useCallback(
    (queryBody) => {
      getRevenue(queryBody);
      getRevenueByCategory(queryBody);
      getOtherIncome(queryBody);
      getOtherIncomeByCategory(queryBody);
      getDirectCosts(queryBody);
      getSalariesAssignedEmployees(queryBody);
      getProjectExpenses(queryBody);
      getGrossIncome(queryBody);
      getIndirectCosts(queryBody);
      getAdministrativePersonnel(queryBody);
      getEmployeesOnBench(queryBody);
      getExpenses(queryBody);
      getExpensesByCategory(queryBody);
      getNetIncome(queryBody);
    },
    [
      getAdministrativePersonnel,
      getDirectCosts,
      getEmployeesOnBench,
      getExpenses,
      getExpensesByCategory,
      getGrossIncome,
      getIndirectCosts,
      getNetIncome,
      getOtherIncome,
      getOtherIncomeByCategory,
      getProjectExpenses,
      getRevenue,
      getRevenueByCategory,
      getSalariesAssignedEmployees,
    ]
  );

  const requestByProject = useCallback(
    (queryBody) => {
      getRevenueByProject(queryBody);
      getRevenueByPC(queryBody);
      getDirectCostsByProject(queryBody);
      getSalariesByProject(queryBody);
      getSalariesBPE(queryBody);
      getExpensesByProject(queryBody);
      getExpensesBPC(queryBody);
      getGrossIncomeByProject(queryBody);
    },
    [
      getDirectCostsByProject,
      getExpensesBPC,
      getExpensesByProject,
      getGrossIncomeByProject,
      getRevenueByPC,
      getRevenueByProject,
      getSalariesBPE,
      getSalariesByProject,
    ]
  );

  useEffect(() => {
    if (controller.current) {
      controller.current.abort();
    }
    controller.current = new AbortController();

    const context = {
      fetchOptions: {
        signal: controller.current.signal,
      },
      queryDeduplication: false,
    };

    if (currency === ALL_CURRENCIES) {
      request({
        variables: { freq: period },
        context,
      });
    } else if (isProjectSelected) {
      requestByProject({
        variables: { currency: currency, freq: period },
        context,
      });
    } else if (!isProjectSelected && currency !== ALL_CURRENCIES) {
      request({
        variables: { currency: currency, freq: period },
        context,
      });
    }
  }, [controller, currency, isProjectSelected, period, request, requestByProject]);

  const getTreeDefault = useCallback(() => {
    return createTreeArr(
      dateColumnsPeriod,
      dataRevenue?.invoices,
      dataRevenueByCategory?.invoices,
      dataOtherIncome?.extraIncome,
      dataOtherIncomeByCategory?.extraIncome,
      dataDirectCosts?.directCosts,
      dataSalariesAssignedEmployees?.salaries,
      dataProjectExpenses?.directCosts,
      dataGrossIncome?.grossIncome,
      dataIndirectCosts?.indirectCosts,
      dataAdministrativePersonnel?.salaries,
      dataEmployeesOnBench?.salaries,
      dataExpenses?.expenses,
      dataExpensesByCategory?.expenses,
      dataNetIncome?.netIncome,
      currency,
      ONE_CURRENCY,
      period
    );
  }, [
    currency,
    dataAdministrativePersonnel,
    dataDirectCosts,
    dataEmployeesOnBench,
    dataExpenses,
    dataExpensesByCategory,
    dataGrossIncome,
    dataIndirectCosts,
    dataNetIncome,
    dataOtherIncome,
    dataOtherIncomeByCategory,
    dataProjectExpenses,
    dataRevenue,
    dataRevenueByCategory,
    dataSalariesAssignedEmployees,
    dateColumnsPeriod,
    period,
  ]);

  const getTreeByProject = useCallback(() => {
    return createTreeArrProject(
      dateColumnsPeriod,
      dataRevenueByProject?.invoices,
      dataRevenueByPC?.invoices,
      dataDirectCostsByProject?.directCosts,
      dataSalariesByProject?.salaries,
      dataSalariesBPE?.salaries,
      dataExpensesByProject?.expenses,
      dataExpensesBPC?.expenses,
      dataGrossIncomeByProject?.grossIncome,
      period
    );
  }, [
    dataDirectCostsByProject,
    dataExpensesBPC,
    dataExpensesByProject,
    dataGrossIncomeByProject,
    dataRevenueByPC,
    dataRevenueByProject,
    dataSalariesBPE,
    dataSalariesByProject,
    dateColumnsPeriod,
    period,
  ]);

  const getTreeAllCurrencies = useCallback(() => {
    return createTreeArrCurrency(
      dateColumnsPeriod,
      dataRevenue?.invoices,
      dataRevenueByCategory?.invoices,
      dataOtherIncome?.extraIncome,
      dataOtherIncomeByCategory?.extraIncome,
      dataDirectCosts?.directCosts,
      dataSalariesAssignedEmployees?.salaries,
      dataProjectExpenses?.directCosts,
      dataGrossIncome?.grossIncome,
      dataIndirectCosts?.indirectCosts,
      dataAdministrativePersonnel?.salaries,
      dataEmployeesOnBench?.salaries,
      dataExpenses?.expenses,
      dataExpensesByCategory?.expenses,
      dataNetIncome?.netIncome,
      period
    );
  }, [
    dataAdministrativePersonnel,
    dataDirectCosts,
    dataEmployeesOnBench,
    dataExpenses,
    dataExpensesByCategory,
    dataGrossIncome,
    dataIndirectCosts,
    dataNetIncome,
    dataOtherIncome,
    dataOtherIncomeByCategory,
    dataProjectExpenses,
    dataRevenue,
    dataRevenueByCategory,
    dataSalariesAssignedEmployees,
    dateColumnsPeriod,
    period,
  ]);

  const getCreatedTree = () => {
    if (!render) return [];

    if (currency === ALL_CURRENCIES) {
      return getTreeAllCurrencies();
    } else if (isProjectSelected) {
      return getTreeByProject();
    } else if (!isProjectSelected && currency !== ALL_CURRENCIES) {
      return getTreeDefault();
    }
  };
  const tree = getCreatedTree();
  return (
    <div>
      <Dashboard
        handlePerception={handlePerception}
        localStorageItem={INCOME_STATEMENT_FILTERS}
        showPercent={showPercent}
        getCurrency={({ currencyLabel, projectSelected }) => {
          const periodLabel = period + 's';
          const searchParams = new URLSearchParams('');
          let currencyParam = '';
          setDateColumnsPeriod(createPeriod(periodLabel));

          if (currencyLabel === ALL_CURRENCIES) {
            setCurrency(ALL_CURRENCIES);
            setIsProjectSelected(projectSelected);
            currencyParam = ALL_CURRENCIES;
            currency !== currencyLabel && setRender(false);
          } else {
            const curr = currencyLabel.match(/\((.*)\)/).pop();

            if (currency !== curr || projectSelected !== isProjectSelected) {
              setRender(false);
            }

            setCurrency(curr);
            setIsProjectSelected(projectSelected);
            currencyParam = curr;
          }

          searchParams.append('period', period);
          searchParams.append('currency', currencyParam);
          searchParams.append('by-project', projectSelected);

          if (searchParams.toString()) {
            setSearchParams(INCOME_STATEMENT_FILTERS, searchParams.toString());
            history.push(`?${searchParams.toString()}`);
          }
        }}
        getPeriod={(periodLabel) => {
          const periodForQuery = periodLabel.toUpperCase().slice(0, -1);
          const searchParams = new URLSearchParams('');
          setPeriod(periodForQuery);
          setDateColumnsPeriod(createPeriod(periodLabel));
          period !== periodForQuery && setRender(false);

          searchParams.append('period', periodForQuery);
          searchParams.append('currency', currency);
          searchParams.append('by-project', isProjectSelected);

          if (searchParams.toString()) {
            setSearchParams(INCOME_STATEMENT_FILTERS, searchParams.toString());
            history.push(`?${searchParams.toString()}`);
          }
        }}
      />
      <TableTree
        treeArr={tree}
        dateColumnsPeriod={tree.length > 0 && render ? extractPeriodArray(tree) : []}
        showPercent={showPercent}
        {...props}
      />
    </div>
  );
}

export default IncomeStatement;
